// src/ConsumerApp/Screens/Home/components/HeroBannersCarousel.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CaretRight from '../../../../assets/icons/CaretRight.svg';

import Hero1Img from '../images/hero1.jpeg';
import Hero2Img from '../images/hero2.jpeg';

const BUSINESS_WHATSAPP_NUMBER = '918639065454';

const DEFAULT_HERO_SLIDES = [
  {
    id: 'hero-delivery',
    image: Hero1Img,
    alt: 'Need it urgently? 30-90 mins direct site delivery',
    title: 'Fast Site Delivery (30-90 mins)',
    actionType: 'navigate',
    actionTarget: '/consumer/account/delivery',
  },
  {
    id: 'hero-rfq',
    image: Hero2Img,
    alt: 'Got bulk material requirement? Share on WhatsApp',
    title: 'Request for Quote on WhatsApp',
    actionType: 'whatsapp',
  },
];

export default function HeroBannersCarousel({ slides = DEFAULT_HERO_SLIDES }) {
  const navigate = useNavigate();
  const trackRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Synchronize active indicator with horizontal scroll position
  const handleScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;

    setCanScrollLeft(scrollLeft > 6);
    setCanScrollRight(scrollLeft < maxScroll - 6);

    // Calculate nearest active slide index
    const slideWidth = el.firstElementChild?.getBoundingClientRect().width || clientWidth;
    const computedIndex = Math.round(scrollLeft / slideWidth);
    setActiveIndex(Math.min(Math.max(computedIndex, 0), slides.length - 1));
  }, [slides.length]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    handleScrollState();
    el.addEventListener('scroll', handleScrollState, { passive: true });
    window.addEventListener('resize', handleScrollState);

    return () => {
      el.removeEventListener('scroll', handleScrollState);
      window.removeEventListener('resize', handleScrollState);
    };
  }, [handleScrollState]);

  // Smooth scroll handler
  const scrollToSlide = (index) => {
    const el = trackRef.current;
    if (!el) return;

    const targetChild = el.children[index];
    if (targetChild) {
      el.scrollTo({
        left: targetChild.offsetLeft,
        behavior: 'smooth',
      });
    }
  };

  const handleNavClick = (direction) => {
    const nextIndex = direction === 'left' ? activeIndex - 1 : activeIndex + 1;
    if (nextIndex >= 0 && nextIndex < slides.length) {
      scrollToSlide(nextIndex);
    }
  };

  // Auto-play timer for mobile/single-view slider
  useEffect(() => {
    if (isHovered || slides.length <= 1) return;

    const interval = setInterval(() => {
      const next = (activeIndex + 1) % slides.length;
      scrollToSlide(next);
    }, 4500);

    return () => clearInterval(interval);
  }, [activeIndex, isHovered, slides.length]);

  const handleBannerAction = (slide) => {
    if (slide.actionType === 'whatsapp') {
      const message = encodeURIComponent(
        'Hi, I would like to request a quote (RFQ) for my material list.'
      );
      window.open(`https://wa.me/${BUSINESS_WHATSAPP_NUMBER}?text=${message}`, '_blank');
    } else if (slide.actionType === 'navigate' && slide.actionTarget) {
      navigate(slide.actionTarget);
    }
  };

  if (!slides || slides.length === 0) return null;

  return (
    <div
      style={styles.container}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <style>{`
        .bb-hero-carousel-shell {
          position: relative;
          width: 100%;
          display: flex;
          align-items: center;
        }

        .bb-hero-carousel-track {
          display: flex;
          gap: 16px;
          width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          scroll-snap-type: x mandatory;
          scroll-behavior: smooth;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
          padding: 2px 0;
          box-sizing: border-box;
        }

        .bb-hero-carousel-track::-webkit-scrollbar {
          display: none;
        }

        /* Desktop View (Dual side-by-side presentation) */
        .bb-hero-slide-item {
          flex: 1 1 calc(50% - 8px);
          min-width: calc(50% - 8px);
          position: relative;
          aspect-ratio: 16 / 7.5;
          border-radius: 14px;
          overflow: hidden;
          cursor: pointer;
          border: 1px solid #E2E8F0;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          scroll-snap-align: start;
          background-color: #F8FAFC;
          box-sizing: border-box;
          transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s ease;
        }

        .bb-hero-slide-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
        }

        .bb-hero-slide-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        /* Navigation Arrows */
        .bb-hero-nav-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background-color: #FFFFFF;
          border: 1px solid #CBD5E1;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.14);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
          padding: 0;
          transition: transform 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
        }

        .bb-hero-nav-arrow:hover {
          background-color: #F8FAFC;
          box-shadow: 0 6px 16px rgba(15, 23, 42, 0.22);
          transform: translateY(-50%) scale(1.05);
        }

        .bb-hero-nav-arrow:active {
          transform: translateY(-50%) scale(0.95);
        }

        .bb-hero-nav-left {
          left: -14px;
        }

        .bb-hero-nav-right {
          right: -14px;
        }

        /* Visual Dots Under Carousel */
        .bb-hero-dots-row {
          display: none;
          align-items: center;
          justify-content: center;
          gap: 6px;
          margin-top: 8px;
          width: 100%;
        }

        .bb-hero-dot-indicator {
          height: 6px;
          width: 6px;
          border-radius: 3px;
          background-color: #CBD5E1;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
          padding: 0;
        }

        .bb-hero-dot-active {
          width: 18px;
          background-color: #E9001E;
        }

        /* ── Tablet & Mobile Aspect Transitions (<= 992px) ── */
        @media (max-width: 992px) {
          .bb-hero-slide-item {
            flex: 1 0 100%;
            min-width: 100%;
            aspect-ratio: 16 / 7;
          }

          .bb-hero-dots-row {
            display: flex;
          }

          .bb-hero-nav-arrow {
            display: none !important;
          }
        }

        /* ── Mobile Screens (<= 640px) ── */
        @media (max-width: 640px) {
          .bb-hero-slide-item {
            aspect-ratio: 16 / 8;
            border-radius: 12px;
          }
        }
      `}</style>

      <div className="bb-hero-carousel-shell">
        {/* Left Arrow */}
        {canScrollLeft && (
          <button
            type="button"
            className="bb-hero-nav-arrow bb-hero-nav-left"
            onClick={() => handleNavClick('left')}
            aria-label="Previous banner"
          >
            <img
              src={CaretRight}
              alt="‹"
              style={{ width: 14, height: 14, transform: 'rotate(180deg)' }}
            />
          </button>
        )}

        {/* Carousel Tracks */}
        <div ref={trackRef} className="bb-hero-carousel-track">
          {slides.map((slide, idx) => (
            <div
              key={slide.id || idx}
              className="bb-hero-slide-item"
              onClick={() => handleBannerAction(slide)}
              title={slide.title || ''}
            >
              <img
                src={slide.image}
                alt={slide.alt || 'Promotional Banner'}
                className="bb-hero-slide-img"
                loading="eager"
              />
            </div>
          ))}
        </div>

        {/* Right Arrow */}
        {canScrollRight && (
          <button
            type="button"
            className="bb-hero-nav-arrow bb-hero-nav-right"
            onClick={() => handleNavClick('right')}
            aria-label="Next banner"
          >
            <img src={CaretRight} alt="›" style={{ width: 14, height: 14 }} />
          </button>
        )}
      </div>

      {/* Visual Pagination Dots (Activated on single-slide view & mobile breakpoints) */}
      <div className="bb-hero-dots-row">
        {slides.map((_, idx) => (
          <button
            key={idx}
            type="button"
            className={`bb-hero-dot-indicator ${idx === activeIndex ? 'bb-hero-dot-active' : ''}`}
            onClick={() => scrollToSlide(idx)}
            aria-label={`Jump to banner ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
};